import { CircleInfoIcon, EyeIcon, SettingsIcon } from "@xola/icons";
import { InlineValuePopover, Input, PopoverList, Switch, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { SwitchField } from "common";
import { useFormikContext } from "formik";
import React, { ChangeEvent, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Experience, ReviewDestination, ReviewDestinationCopy, ReviewPreference } from "types";
import { useVisibility } from "../../hooks/useVisibility";
import { getReviewPreferenceOptions } from "../../models/Experience";
import { Form } from "../Common/Form";
import { NoTranslate } from "../Common/NoTranslate";
import { RadioInput } from "../Common/RadioInput";
import { SecondaryButton } from "../Common/SecondaryButton";
import { PreviewMessageTemplateDrawer } from "../Customers/Templates/PreviewMessageTemplateDrawer";
import { usePreviewReviewTemplate } from "@/hooks/previewReviewReminderHooks";
import { getSeller } from "@/utils/getSeller";

const SMART_REVIEW_TOOLTIP =
    "Customers are always directed to an internal review form. Enable this to request satisfied customers to a public platform to leave their review.";

const THRESHOLD_OPTIONS: Array<{ value: number; label: string }> = [
    { value: 5, label: "Only 5" },
    { value: 4, label: "4 and above" },
    { value: 3, label: "3 and above" },
    { value: 2, label: "2 and above" },
];

const SMART_REVIEW_HIDDEN_DESTINATIONS = new Set([
    ReviewDestination.OFF,
    ReviewDestination.XOLA,
    ReviewDestination.CUSTOM,
]);

export const ReviewForm = ({ experience }: { experience?: Experience }) => {
    const { values, setFieldValue, errors } = useFormikContext<ReviewPreference>();
    const seller = getSeller();
    const history = useHistory();
    const [visible, { toggle }] = useVisibility(false);

    const { mutateAsync: previewTemplate, data: templatePreview } = usePreviewReviewTemplate(experience, () =>
        toggle(),
    );

    const handlePreviewClick = useCallback(() => {
        previewTemplate(null);
    }, [previewTemplate]);

    const handleReviewLinkChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            setFieldValue("reviewLink", e.target.value);
        },
        [setFieldValue],
    );

    const shouldBeDisabled = (option: ReviewDestination) => {
        return (
            (option === ReviewDestination.TRIPADVISOR && !seller.meta?.tripAdvisor) ||
            (option === ReviewDestination.YELP && !seller.meta?.yelp)
        );
    };

    const handleSettingButtonClick = () => {
        history.push("/settings/company#socialLinks");
    };

    const handleDestinationChange = (option: ReviewDestination) => {
        setFieldValue("destination", option);
    };

    const handleSmartReviewToggle = (enabled: boolean) => {
        setFieldValue("enableSmartReview", enabled);
        if (enabled && SMART_REVIEW_HIDDEN_DESTINATIONS.has(values.destination)) {
            setFieldValue("destination", ReviewDestination.GOOGLE);
        }
    };

    const visibleDestinations = getReviewPreferenceOptions().filter(
        (option) => !values.enableSmartReview || !SMART_REVIEW_HIDDEN_DESTINATIONS.has(option),
    );

    const threshold = values.smartReviewThreshold ?? 4;
    const thresholdLabel = THRESHOLD_OPTIONS.find((opt) => opt.value === threshold)?.label ?? "4 and above";
    const [isThresholdPopoverOpen, { toggle: toggleThresholdPopover, hide: hideThresholdPopover }] =
        useVisibility(false);

    const handleThresholdPopoverClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        toggleThresholdPopover();
    };

    const handleThresholdSelect = (value: number) => {
        setFieldValue("smartReviewThreshold", value);
        hideThresholdPopover();
    };

    return (
        <Form>
            <div className="flex flex-col space-y-4 pb-5">
                <div className="flex items-center space-x-2">
                    <Switch
                        size="medium"
                        isChecked={values.sendReviewEmailToAllTravelers}
                        onChange={(value: boolean) => setFieldValue("sendReviewEmailToAllTravelers", value)}
                    />
                    <h5>Collect reviews from each member of the group (if their contact details are available)</h5>
                </div>

                <div className="flex items-center space-x-2">
                    <span className="font-bold">Smart Reviews</span>
                    <Tooltip content={SMART_REVIEW_TOOLTIP} className="ml-2">
                        <CircleInfoIcon />
                    </Tooltip>
                </div>
                <SwitchField size="medium" isChecked={!!values.enableSmartReview} onChange={handleSmartReviewToggle}>
                    {values.enableSmartReview ? (
                        <>
                            Redirect guests to a public review site only if the rating is{" "}
                            <span onClick={(event) => event.stopPropagation()}>
                                <InlineValuePopover
                                    text={thresholdLabel}
                                    placement="bottom"
                                    isOpen={isThresholdPopoverOpen}
                                    classNames={{ children: "!p-0", text: "font-semibold text-primary" }}
                                    onClick={handleThresholdPopoverClick}
                                    onClickOutside={hideThresholdPopover}
                                >
                                    {THRESHOLD_OPTIONS.map((opt) => (
                                        <PopoverList.Item
                                            key={opt.value}
                                            name={String(opt.value)}
                                            onClickItem={() => handleThresholdSelect(opt.value)}
                                        >
                                            <span>{opt.label}</span>
                                        </PopoverList.Item>
                                    ))}
                                </InlineValuePopover>
                            </span>
                        </>
                    ) : (
                        "Redirect guests to a public review site only if the rating is good"
                    )}
                </SwitchField>

                <div className="space-y-2 pb-2">
                    <span className="font-bold">
                        {values.enableSmartReview ? "Smart Review Destination" : "Review Destination"}
                    </span>
                    {visibleDestinations.map((option) => (
                        <div key={option}>
                            <label className="flex w-fit items-center space-x-2">
                                <RadioInput
                                    checked={values.destination === option}
                                    disabled={shouldBeDisabled(option)}
                                    onChange={() => handleDestinationChange(option)}
                                >
                                    <NoTranslate className="mr-2 capitalize text-gray-darker">
                                        {ReviewDestinationCopy[option]}
                                    </NoTranslate>
                                    {shouldBeDisabled(option) && (
                                        <SecondaryButton size="tiny" onClick={handleSettingButtonClick}>
                                            <SettingsIcon />
                                        </SecondaryButton>
                                    )}
                                </RadioInput>
                            </label>

                            {values.destination === ReviewDestination.GOOGLE && option === ReviewDestination.GOOGLE ? (
                                <div className="mb-2 space-y-2">
                                    <a
                                        href="https://support.google.com/business/answer/7035772"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Follow these steps to generate the link
                                    </a>
                                    <br />
                                    <span className={clsx(errors?.reviewLink && "!text-danger", "text-sm font-bold")}>
                                        Review URL {errors?.reviewLink ? ` ${errors.reviewLink}` : ""}
                                    </span>
                                    <Input
                                        required
                                        value={values.reviewLink}
                                        placeholder="Paste review link here"
                                        isError={!!errors?.reviewLink}
                                        onChange={handleReviewLinkChange}
                                    />
                                </div>
                            ) : null}
                        </div>
                    ))}
                </div>

                {[ReviewDestination.OFF, ReviewDestination.CUSTOM].includes(values.destination) ? null : (
                    <SecondaryButton size="small" className="mr-auto" icon={<EyeIcon />} onClick={handlePreviewClick}>
                        Preview
                    </SecondaryButton>
                )}
            </div>
            <PreviewMessageTemplateDrawer isOpen={visible} onClose={toggle}>
                {templatePreview && (
                    <iframe
                        sandbox="allow-same-origin"
                        srcDoc={templatePreview}
                        className={clsx("h-full w-full", { "mt-30": values.destination !== ReviewDestination.CUSTOM })}
                    />
                )}
            </PreviewMessageTemplateDrawer>
        </Form>
    );
};
